import { Maybe } from '@crocodille/util';
import { useCallback, useEffect } from 'react';

import {
  REQUIRED_VALIDATOR_V2,
  useForm,
  useFormField,
  v2_useMaxLengthValidator,
  v2_useMinLengthValidator,
} from '../modules/forms';

export interface AccountISICCardFormOptions {
  readonly initialData?: Maybe<AccountISICCardFormData>;
  onSubmit?(data: AccountISICCardFormData): void;
}

export interface AccountISICCardFormData {
  readonly isicCardNumber: string;
  readonly firstName: Maybe<string>;
  readonly lastName: Maybe<string>;
}

export function useAccountISICCardForm({
  initialData,
  onSubmit,
}: AccountISICCardFormOptions) {
  const isicMinLengthValidator = v2_useMinLengthValidator(10);
  const isicMaxLengthValidator = v2_useMaxLengthValidator(20);

  const isicCardNumber = useFormField({
    value: '',
    required: true,
    validators: [
      REQUIRED_VALIDATOR_V2,
      isicMinLengthValidator,
      isicMaxLengthValidator,
    ],
  });

  useEffect(() => {
    isicCardNumber.reset(initialData?.isicCardNumber ?? '');
  }, [initialData?.isicCardNumber]);

  const nameMinLengthValidator = v2_useMinLengthValidator(2);
  const nameMaxLengthValidator = v2_useMaxLengthValidator(30);

  const firstName = useFormField({
    value: initialData?.firstName ?? '',
    required: true,
    validators: [
      REQUIRED_VALIDATOR_V2,
      nameMinLengthValidator,
      nameMaxLengthValidator,
    ],
  });

  useEffect(() => {
    firstName.reset(initialData?.firstName ?? '');
  }, [initialData?.firstName]);

  const lastName = useFormField({
    value: initialData?.lastName ?? '',
    required: true,
    validators: [
      REQUIRED_VALIDATOR_V2,
      nameMinLengthValidator,
      nameMaxLengthValidator,
    ],
  });

  useEffect(() => {
    lastName.reset(initialData?.lastName ?? '');
  }, [initialData?.lastName]);

  const onSubmitWrapper = useCallback(
    (data: AccountISICCardFormData) => {
      onSubmit?.({
        firstName: data.firstName === '' ? null : data.firstName,
        lastName: data.lastName === '' ? null : data.lastName,
        isicCardNumber: data.isicCardNumber,
      });
    },
    [onSubmit],
  );

  return useForm({
    fields: {
      isicCardNumber,
      firstName,
      lastName,
    },
    onSubmit: onSubmitWrapper,
  });
}
